import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import type { Avatar, BadgeItemType, Maybe } from '@evc/web-components';

import { PlatformConfigService } from '../../../features/config/config.service';
import { I18nPlatformService } from '../../../features/i18n/i18n.service';
import type {
  ApiOrganization,
  ApiOrganizationFetchItem,
  Organization,
} from '../organizations/organizations.type';
import { Permission } from '../types/permission.type';
import { ReadonlyRole, Role } from '../types/role.type';
import { ApiWebApp } from '../types/webapp.type';
import type {
  ApiUser,
  ComputedWebApp,
  LegacyApiUser,
  UserApi,
  UserApiPossibilities,
  UserProfile,
} from '../user/user.type';

const DEFAULT_USER_COLOR = '#93C6F9';
const DEFAULT_USER_CHAR = '.';
const OWNER_ID = '00000000-0000-0000-0000-000000000001';

export type ComputeOrganizationPayload = {
  organizationInfos: Partial<Organization | ApiOrganizationFetchItem | ApiOrganization>;
  userRoles?: Role[];
  availableRoles?: Role[];
};

@Injectable({
  providedIn: 'root',
})
export class CoreClientUtilService {
  #configService = inject(PlatformConfigService);
  readonly #i18n = inject(I18nPlatformService);
  #sanitizer = inject(DomSanitizer);

  /** post api returns different format that the get one
   * here we ensure we have the same format
   */
  computeOrganization({
    organizationInfos,
    availableRoles,
    userRoles,
  }: ComputeOrganizationPayload): Organization {
    const id =
      (organizationInfos as Organization).id ||
      (organizationInfos as ApiOrganizationFetchItem).organizationId;
    const name =
      (organizationInfos as Organization).name ||
      (organizationInfos as ApiOrganizationFetchItem).organizationName ||
      '';
    const phone = (organizationInfos as ApiOrganization).phone || '';
    const addresses = (organizationInfos as ApiOrganization).addresses || [];
    const eTag = (organizationInfos as ApiOrganization).eTag || undefined;
    const logo = (organizationInfos as ApiOrganizationFetchItem).logo;

    if (!name) throw new Error('Organization name is required');

    if (!id) throw new Error('Organization id is required');

    if (!userRoles) userRoles = [];

    if (!availableRoles) availableRoles = [];

    return {
      id,
      name,
      current: (organizationInfos as Organization).current ?? false,
      avatar:
        (organizationInfos as Organization).avatar ?? this.generateOrganizationAvatar(name, logo),
      addresses,
      phone,
      eTag,
      userRoles: this.computeRoles(userRoles),
      availableRoles: this.computeRoles(availableRoles),
      hasLicenseForApplication:
        (organizationInfos as Organization).hasLicenseForApplication ??
        (organizationInfos as ApiOrganizationFetchItem).hasLicenseForApplication,
    };
  }

  computeUserProfile(data: UserApi, permissions: Permission[] = []): UserProfile {
    const user = data as UserApiPossibilities;
    const id = user.id ?? user.tenantId;
    const displayName = [user.givenName, user.surname].join(' ');
    const { email } = user;
    const roles = user.roles ?? (user as LegacyApiUser).role;

    const userImage = (data as ApiUser).profilePicture?.url ?? user.userImage;

    const profile: UserProfile = {
      id,
      displayName,
      givenName: user.givenName ?? '',
      surname: user.surname ?? '',
      email: email?.toLowerCase(),
      emailVerified: user.emailVerified ?? false,
      phone: user.phone ?? '',
      preferredLanguage: user.preferredLanguage,
      address: user.address,
      avatar: this.computeAvatar({ ...user, formalName: displayName, userImage }),
      permissions,
      // TODO : API must return lastUpdatedPassword
      lastUpdatedPassword: user.lastUpdatedPassword,
      roles: this.computeRoles(roles),
    };

    return profile;
  }

  computeRoles(roles: Maybe<ReadonlyRole | Role[]>): Role[] {
    if (!roles) return [];

    if (typeof roles === 'string') {
      return [
        {
          id: '0',
          name: roles,
          isReadOnly: false,
        },
      ];
    }

    if (roles.length < 1) return [];

    return roles;
  }

  getUserRoles(organization: Organization): string {
    if (!organization.userRoles) {
      return '';
    }

    return organization.userRoles.map((role) => role.name).join(', ');
  }

  isOwner(id: Maybe<string>): boolean {
    return id === OWNER_ID;
  }

  formatToBadgeItemType(organization: Organization): BadgeItemType {
    const { id, name, avatar, current } = organization;
    const isCurrent = current || false;
    const badgeItem: BadgeItemType = {
      id,
      name,
      avatar: avatar ?? {
        type: 'initials',
        light: true,
        initials: this.#computeOrgInitials(name),
      },
      additionalText: this.#getOrganizationAdditionalText(organization),
      current: isCurrent,
    };

    return badgeItem;
  }

  #getOrganizationAdditionalText(organization: Organization): string {
    if (organization.hasLicenseForApplication !== false) return this.getUserRoles(organization);

    const { title, name } = this.#configService.getComputed('app')() ?? {};

    return this.#i18n.t('organizations.no_license', { appName: title ?? name ?? '' });
  }

  // *################### Avatars ###################*

  computeAvatar(user: UserApiPossibilities): Avatar {
    const { formalName, userImage, color: avatarBackgroundColor } = user;
    if (userImage) {
      return {
        type: 'image',
        src: userImage,
      };
    }

    const backgroundColor = avatarBackgroundColor ?? DEFAULT_USER_COLOR;

    return {
      type: 'initials',
      color: backgroundColor,
      light: this.#isColorLight(backgroundColor),
      initials: this.#computeUserInitials(formalName!),
    };
  }

  generateOrganizationAvatar(name?: string, logo?: { url: string }): Avatar {
    if (logo) {
      return {
        type: 'image',
        src: logo?.url,
      };
    } else {
      return {
        type: 'initials',
        light: true,
        initials: this.#computeOrgInitials(name ?? ''),
      };
    }
  }

  #computeUserInitials(formalName: string): string {
    const words = formalName.split(' ') ?? [];

    const firstWord = words[0] || DEFAULT_USER_CHAR;
    const lastWord = words.length > 1 ? words[words.length - 1] : undefined;

    const firstLetter = firstWord[0]?.toLocaleUpperCase() || DEFAULT_USER_CHAR;
    const secondLetter = firstWord[1]?.toLocaleLowerCase() || DEFAULT_USER_CHAR;
    const lastInitial = lastWord?.[0]?.toLocaleUpperCase() || secondLetter;

    return firstLetter + lastInitial;
  }

  #computeOrgInitials(name: string): string {
    if (name.length <= 2) return name.toUpperCase();

    return name
      .split(' ')
      .map((word) => word[0].toUpperCase())
      .join('')
      .slice(0, 2);
  }

  #isColorLight(color: string): boolean {
    const hex = color.replace('#', '');

    let r = 0,
      g = 0,
      b = 0;
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    return luminance >= 128;
  }

  // *################### Applications Icon ###################*

  async fetchAppIconFromURL(app: ApiWebApp): Promise<ComputedWebApp> {
    const fallbackUrl = this.#configService.get('fallbackApplicationIcon');

    return this.#fetchSvg(app.icon?.url)
      .catch(() => this.#fetchSvg(fallbackUrl))
      .catch(() => this.#getFallbackSafeSvg())
      .then((iconHtml) => ({ ...app, iconHtml: iconHtml! }));
  }

  async #fetchSvg(url: string | undefined): Promise<SafeHtml> {
    if (!url) throw new Error('#fetchSvg url is required');

    return fetch(url).then(async (response) => {
      const contentType = response.headers.get('Content-Type');
      if (!response.ok || !contentType?.includes('image/svg+xml')) throw new Error();
      const svg = await response.text();

      return this.#sanitizer.bypassSecurityTrustHtml(svg);
    });
  }

  #getFallbackSafeSvg(): SafeHtml {
    const fallbackSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="gray">
        <rect width="100%" height="100%" />
      </svg>`;

    return this.#sanitizer.bypassSecurityTrustHtml(fallbackSvg);
  }
}
